import classNames from "classnames";
import CuratedSearchPromptSection from "./CuratedSearchPromptSection";
import CuratedSearchDesignStepsContainer from "../CuratedSearchDesignSteps/CuratedSearchDesignStepsContainer";
import { useContext, useEffect, useState } from "react";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import {
  AutopilotFilterCreationTypeEnum,
  AutopilotSearchStrategyJobEnum,
  LinkedinSalesNavigatorFilters,
} from "../../../__generated__/graphql";
import AutopilotCuratedLinkedinSalesNavigatorSearchContext from "../../../contexts/AutopilotCuratedLinkedinSalesNavigatorSearchContext";
import useGenerateScoringCriteria from "../../../reactHooks/curatedSearch/useGenerateScoringCriteria";
import CuratedSearchScoringCriteriaContainer from "../CuratedSearchScoringCriteriaContainer";

interface ILsnCuratedSearchPromptCard {
  initialLoading: boolean;
  selectedStrategy: AutopilotSearchStrategyJobEnum | null;
  setSelectedStrategy: (
    strategy: AutopilotSearchStrategyJobEnum | null,
  ) => void;
  suggestedPrompts: string[] | undefined;
  expandPrompt?: string;
  criteriaMinimized: boolean;
  setCriteriaMinimized: (value: boolean) => void;
  filtersMinimized: boolean;
  setFiltersMinimized: (value: boolean) => void;
  remixContext:
    | {
        remixPrompt?: string;
        previousLinkedinSearchFilters?: LinkedinSalesNavigatorFilters;
      }
    | undefined;
}

const LsnCuratedSearchPromptCard = ({
  initialLoading,
  selectedStrategy,
  setSelectedStrategy,
  suggestedPrompts,
  expandPrompt,
  remixContext,
  criteriaMinimized,
  setCriteriaMinimized,
  filtersMinimized,
  setFiltersMinimized,
}: ILsnCuratedSearchPromptCard) => {
  const {
    selectedPrompt,
    setSelectedPrompt,
    startedGeneratingFilters,
    setStartedGeneratingFilters,
    setScoringCriteriaValues,
    expandScoringCriteriaValues,
  } = useContext(AutopilotCuratedSearchContext);

  const { savedFilters, setSavedFilters } = useContext(
    AutopilotCuratedLinkedinSalesNavigatorSearchContext,
  );

  const [generateScoringCriteria, { loading: generateScoringCriteriaLoading }] =
    useGenerateScoringCriteria();

  const [showPlanningStrategy, setShowPlanningStrategy] = useState(
    Boolean(savedFilters),
  );

  useEffect(() => {
    if (!savedFilters) {
      setShowPlanningStrategy(false);
    }
  }, [savedFilters]);

  useEffect(() => {
    if (expandScoringCriteriaValues.length > 0) {
      generateScoringCriteria({
        variables: {
          prompt: selectedPrompt || "",
          expandContext: {
            expandPrompt: expandPrompt || "",
            previousScoringCriteriaValues: expandScoringCriteriaValues,
          },
        },
      });
    }
  }, [
    expandScoringCriteriaValues,
    expandPrompt,
    selectedPrompt,
    generateScoringCriteria,
  ]);

  const handleSubmit = () => {
    setStartedGeneratingFilters(true);
    generateScoringCriteria({
      variables: {
        prompt: selectedPrompt || "",
      },
      onCompleted: (data) => {
        setScoringCriteriaValues(data.generateScoringCriteria);
      },
    });
  };

  return (
    <div className={classNames("flex", "flex-col", "gap-4")}>
      <CuratedSearchPromptSection
        selectedPrompt={selectedPrompt}
        setSelectedPrompt={setSelectedPrompt}
        suggestedPrompts={suggestedPrompts}
        startedGeneratingFilters={startedGeneratingFilters}
        setStartedGeneratingFilters={setStartedGeneratingFilters}
        loading={initialLoading}
        handleSubmit={handleSubmit}
      />
      {startedGeneratingFilters && (
        <>
          <CuratedSearchScoringCriteriaContainer
            isOpen={!criteriaMinimized}
            setIsOpen={() => setCriteriaMinimized(!criteriaMinimized)}
            generateScoringCriteriaLoading={generateScoringCriteriaLoading}
            showPlanningStrategy={showPlanningStrategy}
            setShowPlanningStrategy={setShowPlanningStrategy}
          />
          <CuratedSearchDesignStepsContainer
            isRemixStep={false}
            isOpen={!filtersMinimized}
            setIsOpen={() => setFiltersMinimized(!filtersMinimized)}
            showPlanningStrategy={showPlanningStrategy}
            searchType={AutopilotFilterCreationTypeEnum.LsnGeneralFilters}
            isLsn={true}
            savedFilters={savedFilters}
            setSavedFilters={setSavedFilters}
            selectedStrategy={selectedStrategy}
            setSelectedStrategy={setSelectedStrategy}
            remixContext={remixContext}
          />
        </>
      )}
    </div>
  );
};

export default LsnCuratedSearchPromptCard;
